'use client';

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Company "About" block. A single descriptive summary is shown, chosen by
 * precedence (first non-empty wins):
 *   1. `ownerAboutUs`  — verified company-owner override (definitive)
 *   2. `aiSummary`      — AI summary derived from the financials/balance sheets
 *   3. `websiteSummary` — homepage summary
 *   4. `factsLead`      — deterministic structural sentence (register, seat,
 *                         latest financials); the fallback that guarantees even
 *                         thin pages carry unique, indexable prose.
 *
 * Only one summary renders at a time. The chosen text is ALWAYS rendered into
 * the DOM in full — it is merely clamped to four lines via CSS (`line-clamp`)
 * with a "Show more" toggle, so the complete text stays crawlable for SEO
 * (the toggle only flips a CSS class; it never conditionally mounts the text).
 *
 * `gegenstand` (the official Handelsregister business purpose) is distinct,
 * non-duplicate content and is rendered as its own labeled sibling block.
 *
 * WARNING: `ownerAboutUs` comes from verified company owners (overrides) and
 * MUST take highest priority. When present it is the definitive description and
 * replaces the website summary, AI summary, structural lead, and Gegenstand.
 */
export function AboutSection({
  ownerAboutUs,
  websiteSummary,
  aiSummary,
  ownershipSentence,
  gegenstand,
  factsLead,
  isLoading,
  children,
}: {
  ownerAboutUs?: string | null;
  websiteSummary?: string | null;
  aiSummary?: string | null;
  /** Family/succession verdict as prose (see ownershipSentenceKey) —
   *  appended to the shown summary. Not appended to a verified owner
   *  description, which is definitive and must not be edited. */
  ownershipSentence?: string | null;
  gegenstand?: string | null;
  factsLead?: string | null;
  isLoading?: boolean;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();

  const hasOwner = !!ownerAboutUs?.trim();
  const hasWebsite = !!websiteSummary?.trim();
  const hasAi = !!aiSummary?.trim();
  const hasOwnership = !!ownershipSentence?.trim();
  const hasGegenstand = !!gegenstand?.trim();
  const hasLead = !!factsLead?.trim();
  const hasAny =
    hasOwner || hasWebsite || hasAi || hasOwnership || hasGegenstand || hasLead;

  // Single summary, by precedence: verified owner > AI (balance sheets) >
  // website > structural facts lead.
  const baseSummary = hasOwner
    ? ownerAboutUs!.trim()
    : hasAi
      ? aiSummary!.trim()
      : hasWebsite
        ? websiteSummary!.trim()
        : hasLead
          ? factsLead!.trim()
          : null;
  const summaryText =
    hasOwnership && !hasOwner
      ? [baseSummary, ownershipSentence!.trim()].filter(Boolean).join(' ')
      : baseSummary;

  // The register Gegenstand is shown as its own block whenever present, except
  // when a verified owner description supersedes it.
  const showGegenstand = hasGegenstand && !hasOwner;

  if (isLoading && !hasAny) {
    return (
      <div className="space-y-2">
        <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          {t('companyDetail.about')}
          <Loader2 className="h-3 w-3 animate-spin" />
        </h2>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (!hasAny) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
        {t('companyDetail.about')}
      </h2>

      {summaryText && <ClampedSummary text={summaryText} />}

      {showGegenstand && (
        <div className="space-y-1 pt-1">
          <h3 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
            {t('companyDetail.gegenstandLabel')}
          </h3>
          <ClampedSummary text={gegenstand!.trim()} lines={3} />
        </div>
      )}

      {children && (
        <>
          <h3 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground pt-2">
            {t('companyDetail.classification')}
            <span className="font-normal normal-case tracking-normal ml-1.5 text-muted-foreground">
              WZ 2025
            </span>
          </h3>
          {children}
        </>
      )}
    </div>
  );
}

/**
 * Renders summary text clamped to four lines with a "Show more"/"Show less"
 * toggle. SEO-safe by construction: the full text is always present in the DOM
 * — collapsing only applies the `line-clamp-4` CSS utility. The toggle is only
 * offered when the text actually overflows four lines (measured client-side,
 * so server and initial client render match: no button until after hydration).
 */
function ClampedSummary({ text, lines = 4 }: { text: string; lines?: 3 | 4 }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      // Only meaningful while clamped; when expanded the clamp is off and we
      // keep the toggle visible via `|| expanded` below.
      if (expanded) return;
      setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    measure();

    // Re-measure once web fonts finish loading. Before that the text can take
    // fewer lines and look like it doesn't overflow, which would hide the
    // toggle on initial page load until the next resize.
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(measure).catch(() => {});
    }

    // Observe the element so the toggle reacts to width/layout changes (and the
    // observer's initial callback gives us another measure after first layout).
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <div className="space-y-1">
      <div className="relative">
        <p
          ref={ref}
          className={cn(
            'text-xs sm:text-sm leading-relaxed text-foreground',
            !expanded && (lines === 3 ? 'line-clamp-3' : 'line-clamp-4'),
          )}
        >
          {text}
        </p>
        {/* While clamped, the toggle sits inline on the last (4th) line. It is
            absolutely positioned at the bottom-right with a fade from the card
            background so the trailing text is cut off a bit early to make room. */}
        {!expanded && overflowing && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-expanded={expanded}
            className="absolute bottom-0 right-0 flex items-center bg-gradient-to-r from-transparent via-background to-background pl-32 text-xs sm:text-sm leading-relaxed text-muted-foreground hover:text-foreground"
          >
            … {t('companyDetail.showMore')}
          </button>
        )}
      </div>
      {expanded && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          aria-expanded={expanded}
          className="text-xs sm:text-sm leading-relaxed text-muted-foreground hover:text-foreground"
        >
          {t('companyDetail.showLess')}
        </button>
      )}
    </div>
  );
}
